import React, { useEffect, useRef, useState } from 'react'
import { Co2FiltersTypes, DataFilteredType } from '..'
import { Dropdown } from 'semantic-ui-react'
import dayjs from 'dayjs'
import { Co2SettingsTypeAdmin } from '~/api/services/reporting'
import { useTranslation } from 'react-i18next'
import { FarmersService } from '~/api/services/farmers'
import FarmerGroupsService from '~/api/services/farmerGroups'
import { CO2_LFL_SLUG } from '~/Constants'
import { useDebounce } from 'use-lodash-debounce'
import { handleErrors } from '~/utils/helpers'
import { Nullable } from '~/types'
import { FilterWrapper } from '../style'

type FilterType = {
  id: Co2FiltersTypes,
  placeholder: string
}

interface Props {
  checkedFilters: FilterType[]
  isFetchingCo2Data: boolean
  dataFiltered: Nullable<DataFilteredType>
  co2Settings: Co2SettingsTypeAdmin
  handleChangeData: (data: DataFilteredType) => void
}

const Filters: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const { checkedFilters, isFetchingCo2Data, co2Settings, handleChangeData } = props

  const skipNextFarmerSearch = useRef(false)
  const [loadingFarmersOptions, setLoadingFarmersOptions] = useState(false)
  const [loadingFarmerGroupOptions, setLoadingFarmerGroupOptions] = useState(false)
  const [searchQueryFarmer, setSearchQueryFarmer] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [farmersOptions, setFarmersOptions] = useState([])
  const [farmerGroupsOptions, setFarmerGroupOptions] = useState([])
  const [dataFiltered, setDataFiltered] = useState(props.dataFiltered)

  const debouncedSearchValue = useDebounce(searchQuery, 300);
  const debouncedValueFarmer = useDebounce(searchQueryFarmer, 300)

  useEffect(() => {
    setDataFiltered(props.dataFiltered)
  }, [props.dataFiltered])

  useEffect(() => {
    if (dataFiltered) {
      handleChangeData(dataFiltered)
    }
  }, [dataFiltered])

  useEffect(() => {
    const shouldSearchGroups = checkedFilters.some(filter => filter.id === Co2FiltersTypes.FARMER_GROUP) && dataFiltered?.periodFrom
    const shouldSearchFarmers = checkedFilters.some(filter => filter.id === Co2FiltersTypes.FARMER) && dataFiltered?.periodFrom

    if (shouldSearchGroups && debouncedSearchValue !== undefined) {
      handleSearchFarmerGroups(debouncedSearchValue)
    }
    if (shouldSearchFarmers) {
      if (skipNextFarmerSearch.current) {
        skipNextFarmerSearch.current = false
      } else {
        handleSearchFarmers()
      }
    }
  }, [
    debouncedSearchValue,
    debouncedValueFarmer,
    dataFiltered?.periodFrom,
    dataFiltered?.periodTo
  ])

  async function handleSearchFarmerGroups(searchQuery) {
    setLoadingFarmerGroupOptions(true)
    try {
      const response = await FarmerGroupsService.getFarmerGroupsList(
        CO2_LFL_SLUG,
        searchQuery,
        {
          periodFrom: dataFiltered?.periodFrom || '',
          periodTo: dataFiltered?.periodTo || ''
        }
      )
      const options = response.data.map(item => ({ value: item.id, text: item.name }))
      setFarmerGroupOptions(options)
      setLoadingFarmerGroupOptions(false)
    } catch (error) {
      setLoadingFarmerGroupOptions(false)
      handleErrors(error)
    }
  }

  async function handleSearchFarmers(dataFilteredNew = null) {
    setLoadingFarmersOptions(true)
    try {
      const dataFilteredPayload = dataFilteredNew ?? dataFiltered
      const response = await FarmersService.getFarmers({
        farmerGroupId: dataFilteredPayload?.farmerGroupId,
        search: searchQueryFarmer,
        periodFrom: dataFilteredPayload?.periodFrom || '',
        periodTo: dataFilteredPayload?.periodTo || '',
        page: 0
      })
      const options = response.data.map(item => ({ value: item.id, text: `${item.companyName} (${item.supplierNo})` }))
      if (options.length === 1 && searchQueryFarmer === '') {
        setDataFiltered({
          ...dataFilteredPayload,
          farmerId: options[0].value
        })
      } else if (dataFilteredPayload?.farmerId) {
        setDataFiltered({
          ...dataFilteredPayload,
          farmerId: ''
        })
      }
      setFarmersOptions(options)
      setLoadingFarmersOptions(false)
    } catch (error) {
      setLoadingFarmersOptions(false)
      setFarmersOptions([])
      handleErrors(error)
    }
  }

  async function handleChangeFarmerGroup(id) {
    setLoadingFarmersOptions(true)
    setDataFiltered({
      ...dataFiltered,
      farmerGroupId: id,
      farmerId: ''
    })
    setSearchQueryFarmer('')
    try {
      await handleSearchFarmers({
        ...dataFiltered,
        farmerGroupId: id,
        farmerId: ''
      })
    } catch (error) {
      handleErrors(error)
    }
    setLoadingFarmersOptions(false)
  }

  function renderCheckedFilter(checkedFilter: FilterType) {
    switch (checkedFilter.id) {
      case Co2FiltersTypes.DATA_COLLECTIONS:
        return <Dropdown
          placeholder={t(checkedFilter.placeholder)}
          selectOnBlur={false}
          id={checkedFilter.id}
          key={checkedFilter.id}
          selection
          disabled={isFetchingCo2Data}
          style={{ minWidth: 260 }}
          value={dataFiltered?.co2FootprintId}
          search
          onChange={(_, data) => setDataFiltered({
            ...dataFiltered,
            co2FootprintId: data.value as string
          })}
          clearable
          options={co2Settings?.co2Footprints?.map(co2Footprint => ({
            text: `${co2Footprint.title} (${co2Footprint.uniqueId})`,
            value: co2Footprint.id
          })) || []}
        />
      case Co2FiltersTypes.DATA_TYPES:
        return <Dropdown
          key={checkedFilter.id}
          placeholder={t(checkedFilter.placeholder)}
          selectOnBlur={false}
          id={checkedFilter.id}
          disabled={isFetchingCo2Data}
          selection
          onChange={(_, data) => setDataFiltered({
            ...dataFiltered,
            resultType: data.value as string
          })}
          value={dataFiltered?.resultType}
          options={co2Settings?.resultTypes?.map(resultType => ({ text: resultType.label, value: resultType.key })) || []}
        />
      case Co2FiltersTypes.FARMER:
        return <Dropdown
          key={checkedFilter.id}
          noResultsMessage={searchQuery !== '' ? t('No results found') : t('Please enter 1 or more character')}
          selection
          selectOnBlur={false}
          search={(options, query) => {
            return options
          }}
          clearable
          id={checkedFilter.id}
          searchQuery={searchQueryFarmer}
          loading={loadingFarmersOptions}
          disabled={isFetchingCo2Data || loadingFarmerGroupOptions}
          options={farmersOptions || []}
          placeholder={t('Search farmers')}
          value={dataFiltered?.farmerId}
          onSearchChange={(e, { searchQuery }) => setSearchQueryFarmer(searchQuery)}
          onChange={(e, data) => {
            if (!data.value) {
              const newDataFiltered = { ...dataFiltered, farmerId: '' }
              setDataFiltered(newDataFiltered)
              handleSearchFarmers(newDataFiltered)
            } else {
              skipNextFarmerSearch.current = true
              setSearchQueryFarmer('')
              setDataFiltered({ ...dataFiltered, farmerId: data.value as string })
            }
          }}
        />
      case Co2FiltersTypes.FARMER_GROUP:
        return <Dropdown
          key={checkedFilter.id}
          noResultsMessage={searchQuery !== '' ? t('No results found') : t('Please enter 1 or more character')}
          selection
          selectOnBlur={false}
          search={(options, query) => {
            return options
          }}
          clearable
          searchQuery={searchQuery}
          id={checkedFilter.id}
          disabled={isFetchingCo2Data}
          loading={loadingFarmerGroupOptions}
          options={farmerGroupsOptions || []}
          placeholder={t('Search farmer group')}
          value={dataFiltered?.farmerGroupId}
          onSearchChange={(e, { searchQuery }) => setSearchQuery(searchQuery)}
          onChange={(e, data) => handleChangeFarmerGroup(data.value)}
        />
      case Co2FiltersTypes.INDIVIDUAL_FILTERS:
        return <Dropdown
          key={checkedFilter.id}
          selectOnBlur={false}
          disabled={isFetchingCo2Data}
          id={checkedFilter.id}
          selection
          value={dataFiltered?.customFilterId}
          placeholder={t(checkedFilter.placeholder)}
          clearable
          onChange={(_, data) => setDataFiltered({
            ...dataFiltered,
            customFilterId: data.value as string
          })}
          options={co2Settings?.customFilters?.map(customFilter => ({ text: customFilter.name, value: customFilter.id })) || []}
        />
      case Co2FiltersTypes.PERIOD:
        return <Dropdown
          key={checkedFilter.id}
          placeholder={t(checkedFilter.placeholder)}
          disabled={isFetchingCo2Data}
          id={checkedFilter.id}
          selectOnBlur={false}
          selection
          onChange={(_, { value }) => {
            const periods = value.toString().split('/')
            setDataFiltered({
              ...dataFiltered,
              periodFrom: periods[0],
              periodTo: periods[1]
            })
          }}
          value={dataFiltered?.periodFrom ? `${dataFiltered?.periodFrom}/${dataFiltered?.periodTo}` : ''}
          options={co2Settings?.periods?.map(period => ({
            text: `${dayjs(period.periodFrom).format('DD.MM.YYYY')} - ${dayjs(period.periodTo).format('DD.MM.YYYY')}`,
            value: `${period.periodFrom}/${period.periodTo}`
          })) || []}
        />
      case Co2FiltersTypes.RACE:
        return <Dropdown
          key={checkedFilter.id}
          selectOnBlur={false}
          disabled={isFetchingCo2Data}
          id={checkedFilter.id}
          selection
          selectOnNavigation={false}
          value={dataFiltered?.breedId}
          placeholder={t(checkedFilter.placeholder)}
          defaultValue={null}
          $selectedBreed={Boolean(dataFiltered?.breedId)}
          clearable
          onChange={(_, data) => setDataFiltered({
            ...dataFiltered,
            breedId: data.value as string
          })}
          options={co2Settings?.breeds?.map(breed => ({ text: breed.label, value: breed.id })) || []}
        />
      default:
        return <></>
    }
  }

  function renderFilters() {
    return checkedFilters.map(checkedFilter => {
      return renderCheckedFilter(checkedFilter)
    })
  }

  return <FilterWrapper>
    {renderFilters()}
  </FilterWrapper>
}
export default Filters